import { useSip } from "@/contexts/SipContext";
import { 
  Phone, 
  PhoneOff,
  PhoneIncoming
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

export default function IncomingCallModal() {
  const { 
    callStatus, 
    incomingCallInfo, 
    callerDetails,
    answerCall, 
    rejectCall 
  } = useSip();

  // Only show when status is explicitly "incoming" AND we have call info
  const isOpen = callStatus === "incoming" && incomingCallInfo !== null;

  const handleAnswer = async () => {
    // Answer the call - don't navigate away
    // The floating call widget will appear if user is not on dialer page
    await answerCall();
  };

  const handleReject = () => {
    rejectCall();
  };

  // Get the best available name
  const displayName = callerDetails?.name || incomingCallInfo?.name || "Unknown Caller";
  const displayNumber = incomingCallInfo?.number || "";

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        aria-describedby="incoming-call-description"
      >
        {/* Hidden title for accessibility */}
        <VisuallyHidden>
          <DialogTitle>Incoming Call</DialogTitle>
        </VisuallyHidden>
        <DialogDescription id="incoming-call-description" className="sr-only">
          Incoming call from {displayName}
        </DialogDescription>
        
        <div className="flex flex-col items-center py-6">
          {/* Animated incoming call indicator */}
          <div className="relative mb-6">
            <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center animate-pulse">
              <div className="w-20 h-20 rounded-full bg-primary/30 flex items-center justify-center">
                <PhoneIncoming className="w-10 h-10 text-primary animate-bounce" />
              </div>
            </div>
            {/* Ripple effect */}
            <div className="absolute inset-0 rounded-full border-2 border-primary/50 animate-ping" />
          </div>

          {/* Caller info */}
          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">
              Incoming Call
            </p>
            <h2 className="text-2xl font-heading font-bold mb-1">
              {displayName}
            </h2>
            <p className="text-lg text-muted-foreground font-mono">
              {displayNumber}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-6">
            <Button
              variant="destructive"
              size="lg"
              onClick={handleReject}
              className="w-16 h-16 rounded-full p-0"
              data-testid="reject-call-btn"
            >
              <PhoneOff className="w-7 h-7" />
            </Button>
            
            <Button
              variant="default"
              size="lg"
              onClick={handleAnswer}
              className="w-16 h-16 rounded-full p-0 bg-green-600 hover:bg-green-700"
              data-testid="answer-call-btn"
            >
              <Phone className="w-7 h-7" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
